import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useSession } from 'components/Authenticated';
import { txt } from 'libs/i18n';
import ResponsiveTable, { useTableView } from 'components/ResponsiveTable';
import { TeamIcon } from 'components/CustomIcons';
import { App, Button, Form, Input, Spin, Tooltip, Modal, Typography, Alert, Switch, } from 'antd';
import FormModal from 'components/FormModal';
import { DeleteOutlined, EyeInvisibleOutlined, EyeOutlined, InfoCircleOutlined, PlusOutlined, TeamOutlined, } from '@ant-design/icons';
import IconButton from 'components/IconButton';
import DeleteModal from 'components/DeleteModal';
import { addTeamMember, deleteTeamMember, fetchCurrentTeamMembers, fetchSubscriptions, transferOwnership, } from 'api/team-api';
import LanguageSelect from 'components/LanguageSelect';
import { getUiLocales } from 'libs/locales/ui';
import TransferForm from 'components/TransferForm';
import { BYTES_IN_GIGABYTE } from 'libs/utils';
import { PLAN_KEY_BEEY_START } from 'api/model/subscription-plan';
import styles from './style.module.less';
import PlanUsage from '../CreditInfo/TeamPlan/PlanUsage';
const TEAM_MEMBERS_TABLE_ID = 'team-members-table'; // Must be unique across all tables in Beey!!
export const convertToTeamSubscription = (subscription, members) => {
    const { planMetadata } = subscription.items[0];
    const totalStorage = planMetadata.storageLimit;
    const totalNumberOfUsers = planMetadata.teamMembersLimit;
    const currentStorage = members !== 'loading'
        ? members.list.reduce((acc, member) => acc + member.storageSharedBytes, 0) / BYTES_IN_GIGABYTE
        : 0;
    return Object.assign(Object.assign({}, subscription), { totalStorage,
        currentStorage, currentNumberOfUsers: members !== 'loading' ? members.totalCount : 0, totalNumberOfUsers });
};
const TeamMembers = ({ ownerEmail }) => {
    var _a;
    const [teamMembers, setTeamMembers] = useState('loading');
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addMemberLoading, setAddMemberLoading] = useState(false);
    const [deleteModal, setDeleteModal] = useState({
        open: false,
        memberId: 0,
        memberEmail: '',
    });
    const [setupTokenLink, setSetupTokenLink] = useState(null);
    const [teamSubscription, setTeamSubscription] = useState('loading');
    const { session, refetchLogin } = useSession();
    const [form] = Form.useForm();
    const sendEmailChecked = (_a = Form.useWatch('sendEmail', form)) !== null && _a !== void 0 ? _a : true;
    const { message } = App.useApp();
    const tableView = useTableView(TEAM_MEMBERS_TABLE_ID, {
        pageSize: 10,
    });
    const { currentPage, pageSize } = tableView.getViewParams();
    const isTeamOwner = session.login.hasClaim('team:owner');
    const getTeamMembers = async () => {
        const members = await fetchCurrentTeamMembers(session.connection, pageSize * (currentPage - 1), pageSize);
        setTeamMembers(members);
        return members;
    };
    useEffect(() => {
        const fetchTeamSubscriptions = async () => {
            const members = await getTeamMembers();
            const result = await fetchSubscriptions(session.connection);
            if (result.isSuccess()) {
                const apiSubscription = result.get().list.filter((sub) => sub.isActive)[0];
                const extendedSubscription = convertToTeamSubscription(apiSubscription, members);
                setTeamSubscription(extendedSubscription);
            }
        };
        void fetchTeamSubscriptions();
    }, []);
    const createMember = async (memberInfo) => {
        const { email, language: { code }, sendEmail } = memberInfo;
        setAddMemberLoading(true);
        try {
            const result = await addTeamMember(session.connection, email, code, sendEmail);
            if (result.isSuccess()) {
                const response = result.get();
                void getTeamMembers();
                void message.success(txt('memberCreated'));
                setAddModalOpen(false);
                if (sendEmail === false && response.password.passwordSetupToken !== undefined) {
                    setSetupTokenLink({ token: response.password.passwordSetupToken, revealed: false });
                }
            }
            else if (result.err() === 'duplicate-email') {
                void message.error(txt('alreadyExists'));
            }
            else {
                void message.error(txt('failedToAddMember'));
            }
        }
        finally {
            setAddMemberLoading(false);
        }
    };
    const deleteMember = async () => {
        const result = await deleteTeamMember(session.connection, deleteModal.memberId);
        if (result.isSuccess()) {
            void getTeamMembers();
            void message.success(txt('memberDeleted'));
            setDeleteModal({ memberEmail: '', memberId: 0, open: false });
        }
        else {
            void message.error(txt('failedToDeleteMember'));
        }
    };
    if (teamSubscription === 'loading') {
        return _jsx(Spin, {});
    }
    const subscriptionType = teamSubscription.items[0].planKey;
    const unlimitedMembers = session.login.hasClaim('unlimitedTeamSize:enabled');
    const canCreateMembers = subscriptionType !== PLAN_KEY_BEEY_START || unlimitedMembers;
    const remainingMembers = teamSubscription.totalNumberOfUsers
        - teamSubscription.currentNumberOfUsers;
    const options = teamMembers !== 'loading' ? teamMembers.list
        .filter((member) => !member.isOwner)
        .map((member) => ({
        label: member.email,
        value: member.id,
    })) : [];
    const handleTransfer = async (newOwnerId) => {
        if (newOwnerId !== undefined) {
            await transferOwnership(session.connection, newOwnerId);
        }
        void getTeamMembers();
        void refetchLogin();
    };
    const columnOfMembers = [
        {
            title: txt('members'),
            dataIndex: 'email',
            key: 'Email',
        },
        {
            title: '',
            render: (member) => (isTeamOwner && !member.isOwner
                ? (_jsx(Tooltip, { title: txt('delete'), children: _jsx(IconButton, { danger: true, onClick: () => setDeleteModal({
                            open: true,
                            memberId: member.id,
                            memberEmail: member.email,
                        }), children: _jsx(DeleteOutlined, {}) }) })) : null),
        },
    ];
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.teamInfo, children: [_jsxs("div", { className: styles.title, children: [_jsx(TeamIcon, {}), _jsx("h2", { children: txt('teamMembers') }), isTeamOwner && canCreateMembers ? (_jsx(Button, { type: "primary", size: "small", icon: _jsx(PlusOutlined, {}), onClick: () => setAddModalOpen(true), disabled: remainingMembers <= 0 && !unlimitedMembers, children: txt('addMember') })) : null] }), teamMembers !== 'loading' && (_jsxs("div", { children: [_jsxs("span", { className: styles.label, children: [txt('teamOwner'), ":"] }), ' ', _jsx("span", { children: ownerEmail !== 'loading' ? ownerEmail : _jsx(Spin, {}) })] }))] }), isTeamOwner && canCreateMembers && !unlimitedMembers ? (_jsxs("div", { className: styles.planUsage, children: [_jsxs("p", { className: styles.label, children: [txt('youCanAddMembers'), ":"] }), _jsx(PlanUsage, { icon: _jsx(TeamOutlined, {}), total: teamSubscription.totalNumberOfUsers, remaining: remainingMembers < 0 ? 0 : remainingMembers, unit: txt('fromUsers').toLowerCase(), decimal: false })] })) : null, _jsx(ResponsiveTable, { onViewChange: getTeamMembers, className: styles.table, columns: columnOfMembers, dataSource: teamMembers === 'loading' ? [] : teamMembers.list, rowKey: (teamMember) => teamMember.id, tableView: tableView, recordCount: teamMembers === 'loading' ? 0 : teamMembers.totalCount, loading: teamMembers === 'loading', pageSizeOptions: ['10', '20', '50', '100'] }), options.length > 0 && isTeamOwner ? (_jsx(TransferForm, { options: options, onHandleTransfer: handleTransfer, tooltipDescription: txt('confirmTransferOwner'), title: txt('transferOwnership'), validatorText: txt('chooseMember') })) : null, _jsx(FormModal, { open: addModalOpen, title: txt('addMember'), onCancel: () => setAddModalOpen(false), onOk: () => form.submit(), afterClose: () => form.resetFields(), width: "32rem", confirmLoading: addMemberLoading, children: _jsxs(Form, { form: form, onFinish: createMember, labelCol: { span: 8 }, wrapperCol: { span: 14 }, labelAlign: "left", children: [_jsx(Form.Item, { name: "email", label: txt('email'), rules: [
                                {
                                    required: true,
                                    message: txt('pleaseEmail'),
                                    type: 'email',
                                },
                                {
                                    max: 254,
                                    message: txt('maxCharacters'),
                                },
                                {
                                    validator: async (_, value) => {
                                        if (value !== undefined) {
                                            const trimmedEmail = value.trim();
                                            const isEmailTaken = teamMembers !== 'loading' && teamMembers.list.some((member) => member.email.toLowerCase() === trimmedEmail.toLowerCase());
                                            if (value !== '' && isEmailTaken) {
                                                return Promise.reject(txt('alreadyRegistered'));
                                            }
                                            return Promise.resolve();
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ], children: _jsx(Input, { autoFocus: true }) }), _jsx(Form.Item, { label: txt('infoEmail'), name: "sendEmail", valuePropName: "checked", initialValue: true, labelCol: { span: 12 }, wrapperCol: { span: 10 }, tooltip: {
                                title: sendEmailChecked === true ? txt('sendEmailInfoChecked') : txt('sendEmailInfoUnchecked'),
                                icon: _jsx(InfoCircleOutlined, {}),
                            }, children: _jsx(Switch, { size: "small" }) }), _jsx(Form.Item, { name: "language", label: txt('language'), rules: [
                                {
                                    required: true,
                                    message: txt('pleaseLanguage'),
                                },
                            ], children: _jsx(LanguageSelect, { locales: getUiLocales() }) })] }) }), _jsx(DeleteModal, { visible: deleteModal.open, onCancel: () => setDeleteModal(Object.assign(Object.assign({}, deleteModal), { open: false })), email: deleteModal.memberEmail, isTeamOwner: false, onDelete: async () => deleteMember(), redirectTo: "/account/members" }), setupTokenLink !== null && (_jsxs(Modal, { title: txt('setPasswordTokenTitle'), open: true, footer: [
                    _jsx(Button, { type: "primary", onClick: () => {
                            setSetupTokenLink(null);
                        }, children: txt('close') }, "close"),
                ], onCancel: () => {
                    setSetupTokenLink(null);
                }, children: [_jsx(Alert, { type: "warning", message: txt('setPasswordTokenWarning'), showIcon: true }), _jsx("p", { style: { marginBottom: 8 }, children: txt('setPasswordTokenInstructions') }), _jsxs("div", { style: {
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 8,
                            marginTop: 16,
                        }, children: [_jsx(Typography.Paragraph, { copyable: setupTokenLink.revealed
                                    ? { tooltips: [txt('copyToClipboard'), txt('textCopied')] }
                                    : false, style: { wordBreak: 'break-all', marginBottom: 0, flex: 1 }, children: setupTokenLink.revealed
                                    ? `${window.location.origin}/login/setpassword#token=${encodeURIComponent(setupTokenLink.token)}`
                                    : '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••' }), _jsx(Tooltip, { title: txt('revealToken'), children: _jsx(Button, { type: "text", icon: setupTokenLink.revealed ? _jsx(EyeInvisibleOutlined, {}) : _jsx(EyeOutlined, {}), onClick: () => {
                                        setSetupTokenLink((prev) => (prev !== null ? Object.assign(Object.assign({}, prev), { revealed: !prev.revealed }) : null));
                                    } }) })] })] }))] }));
};
export default TeamMembers;
